import type { Store } from 'vuex';
import { consumePortalHandoff } from '@/api/basecamp.api';
import type { ICockpitStore } from '@/store';
import type { IPublicCockpitConfig } from '@/types';

/** A return URL is only honored when it points at the RAM origin (prevents open redirects). */
export const isAllowedPortalReturnUrl = (rawUrl: string, ramUrl: string | undefined): boolean => {
    if (!ramUrl) {
        return false;
    }
    try {
        return new URL(rawUrl).origin === new URL(ramUrl).origin;
    } catch {
        return false;
    }
};

export const isAbsoluteUrl = (value: string): boolean => /^https?:\/\//i.test(value);

/**
 * Consume a portal-handoff token created by basecamp's `POST /mfhs/:mfhId/grant-portal-view`.
 * Must run after the authenticated HTTP clients are initialized and **before** the effective user
 * is resolved, so the very first authenticated calls already carry `x-impersonate-user` and the
 * boot lands in the external partner portal. The backend only releases the payload to the same
 * internal user the token was issued to, and burns the token on first use.
 */
export const applyPortalHandoff = async (store: Store<ICockpitStore>, publicConfig: IPublicCockpitConfig, handoffToken: string): Promise<void> => {
    try {
        // Drop any stale impersonation first: consume must run as the real internal user (the token
        // is bound to them), not as a leftover impersonated principal from a previous session.
        store.commit('generalData/setImpersonatedUserId', null);
        const { partnerPreviewAccountUid, returnUrl } = await consumePortalHandoff(handoffToken);
        if (returnUrl && isAllowedPortalReturnUrl(returnUrl, publicConfig.S_RAM_URL)) {
            localStorage.setItem('__COCKPIT_RETURN_ROUTE__', returnUrl);
        }
        store.commit('generalData/setImpersonatedUserId', partnerPreviewAccountUid);
    } catch (error) {
        // Expired/replayed/foreign token: continue the normal internal boot.
        console.warn('Failed to consume portal handoff token', error);
    }
};
